import { styled } from 'styled-components';
import { ActionsBar, ScrolledTable, useInjectLocalFilters, YColumnsType } from '@ydistri/ds';
import { computeRemSize } from '@ydistri/theme';
import React, { useCallback, useMemo } from 'react';
import { Units } from '../../../lib/charts/saleChartsTypes';
import { useSelector } from 'react-redux';
import { useDetail } from '../../../screens/Detail/hooks/useDetail';
import { useSearchParams } from 'react-router';
import { useAppDispatch } from '../../../store';
import {
  EntityColumns,
  EntityColumnsConfig,
  useStoreTableColumnsProvider,
} from '../../../hooks/useEntityTableColumnsProvider';
import { setIsLoadingSalesData } from '../../../screens/Detail/detailSlice';
import { UrlParams } from '../../../screens/Detail/detailTypes';
import SKUTableColumnHeaderTitle from '../SKUTable/SKUTableColumnHeaderTitle';
import { formatDate, formatNumber } from '@ydistri/generic-utils';
import ShowSKUAsMain from '../SKUTable/actions/ShowSKUAsMain';
import ScrolledTableWrapper from '../../global/ScrolledTableWrapper/ScrolledTableWrapper';
import { SkuDetails } from './skuDetailsTableLib';
import { createDebugLog } from '@ydistri/utils';
import clamp from 'lodash/clamp';

const debugLog = createDebugLog('SkuDetailsTable');

const frequencyMonthsTitle = ['Freq.', '(6 mos)'];
const tableRowHeightPx = 32;

interface SkuDetailsTableProps<T extends SkuDetails> {
  data: T[];
  currency: string;

  /**
   * Whether to show store columns (Store Code and Store Name). Defaults to true.
   */
  showStoreColumns?: boolean;

  /**
   * Optional callback to customize columns after they are created.
   * Receives the default columns and should return the modified columns.
   * @param columns - The default columns generated by the table
   * @returns Modified columns array
   */
  customizeColumns?: (columns: YColumnsType<T>[]) => YColumnsType<T>[];
  /**
   * Optional expandable configuration for the table.
   * If not provided, rows will not be expandable.
   */
  expandable?: React.ComponentProps<typeof ScrolledTable<T>>['expandable'];
  /**
   * Optional callback to provide custom row class names.
   * Receives the default class name as the third parameter, allowing you to build upon it.
   * @param record - The row data
   * @param index - The row index
   * @param defaultClassName - The default class name (disabled, highlightedRow, or empty string)
   * @returns The final class name to apply
   */
  rowClassName?: (record: T, index: number, defaultClassName: string) => string;
}

/**
 * Compute the minimum height for the table based on the number of rows, with a minimum of 120px and a maximum of 500px.
 * @param rowCount Number of rows in the table
 */
const computeMinimumHeight = (rowCount: number): number => {
  return clamp(rowCount * tableRowHeightPx, 120, 500);
};

const StyledSkuDetailsTable = styled(ScrolledTable)<{ $isExpandable?: boolean }>`
  .ant-table-small .ant-table-tbody > tr:not(:first-child) > td {
    padding-top: ${computeRemSize(4)} !important;
    padding-bottom: ${computeRemSize(4)} !important;
  }

  .ant-table-row {
    ${p => p.$isExpandable && 'cursor: pointer;'}

    &.highlightedRow {
      background-color: ${p => p.theme.item.selected.backgroundColor} !important;

      & > td {
        background-color: ${p => p.theme.item.selected.backgroundColor} !important;
      }
    }

    &.expandedRow {
      font-weight: bold;
      background-color: ${p => p.theme.item.selected.backgroundColor} !important;

      & > td {
        border-bottom: 0;
        background-color: ${p => p.theme.item.selected.backgroundColor} !important;
      }

      &:hover > td {
        background: ${p => p.theme.item.selected.backgroundColor} !important;
      }
    }
  }

  .ant-table-expanded-row {
    & > td {
      background-color: ${p => p.theme.item.selected.backgroundColor} !important;
    }
  }

  .disabled {
    color: ${p => p.theme.colors.textDisabled};
    background-color: ${p => p.theme.colors.backgroundDisabled};
  }
`;

/**
 * Based on Units return either saleQuantityTotal or saleValueTotal
 * @param record
 * @param units
 */
function getTotalSalesValue(record: SkuDetails, units: Units) {
  let value: number | undefined = 0;
  switch (units) {
    case Units.QUANTITY:
      value = record.saleQuantityTotal;
      break;
    case Units.VALUE:
      value = record.saleValueTotal;
      break;
  }
  return value;
}

/**
 * SkuDetailsTable - A reusable table component for displaying SKU details
 *
 * This component provides a standard table layout for displaying SKU information with
 * commonly used columns (Store, SKU Class, Available Supply, Frequency, Total Sales, etc.).
 *
 * ## Column Customization
 *
 * You can customize the columns using the `customizeColumns` callback prop. This allows you to:
 * - Add new columns at any position
 * - Remove existing columns
 * - Modify column properties (width, title, sorter, etc.)
 * - Reorder columns
 *
 * @example
 * ```tsx
 * const customizeColumns = useCallback(
 *   (defaultColumns: YColumnsType<MySkuType>[]) => {
 *     // Find where to insert the new column
 *     const skuClassIndex = defaultColumns.findIndex(col => col.dataIndex === ['skuClass', 'name']);
 *     const insertIndex = skuClassIndex !== -1 ? skuClassIndex : 3;
 *
 *     // Create a new column
 *     const sizeColumn: YColumnsType<MySkuType> = {
 *       title: 'Size',
 *       dataIndex: 'productSize',
 *       width: '8rem',
 *       sorter: (left, right) => left.productSize?.localeCompare(right.productSize ?? '') ?? 0,
 *     };
 *
 *     // Insert the column
 *     const modifiedColumns = [...defaultColumns];
 *     modifiedColumns.splice(insertIndex, 0, sizeColumn);
 *
 *     return modifiedColumns;
 *   },
 *   [],
 * );
 *
 * return <SkuDetailsTable data={data} currency={currency} customizeColumns={customizeColumns} />;
 * ```
 */
const SkuDetailsTable = <T extends SkuDetails>(
  props: SkuDetailsTableProps<T>,
): React.JSX.Element => {
  const {
    data,
    currency,
    customizeColumns,
    expandable: customExpandable,
    rowClassName: customRowClassName,
    showStoreColumns = true,
  } = props;
  const units = useSelector(state => state.detailReducer.chartSettings.unit);
  //some attributes are moved to the top level of the object for easier access in the table (for filtering and sorting)

  const { skuId } = useDetail('skuId');
  const [, setSearchParameters] = useSearchParams();
  const dispatch = useAppDispatch();
  const storeColumnProvider = useStoreTableColumnsProvider();

  const showSkuAsMain = useCallback(
    (skuId: number) => {
      debugLog('setting SKU as main', skuId);
      dispatch(setIsLoadingSalesData(true));

      setSearchParameters(prevParams => {
        if (prevParams.has(UrlParams.PRODUCT)) {
          prevParams.delete(UrlParams.PRODUCT);
        }
        if (prevParams.has(UrlParams.STORE)) {
          prevParams.delete(UrlParams.STORE);
        }

        prevParams.set(UrlParams.SKU, skuId.toString());

        return prevParams;
      });
    },
    [dispatch, setSearchParameters],
  );

  const columns: YColumnsType<T>[] = useMemo(() => {
    let storeColumns: YColumnsType<T>[] = [];
    if (showStoreColumns) {
      const storesColumnConfig: EntityColumnsConfig<T> = {
        [EntityColumns.CODE]: {
          width: '8rem',
          title: <SKUTableColumnHeaderTitle title="Store Code" />,
        },
        [EntityColumns.NAME]: {
          width: '14rem',
          defaultSortOrder: 'ascend',
        },
        [EntityColumns.ID]: {
          width: '10rem',
        },
      };

      storeColumns = storeColumnProvider(storesColumnConfig, 'store', 'Store');
    }

    return [
      ...storeColumns,
      {
        title: 'SKU Class',
        dataIndex: ['skuClass', 'name'],
        width: '8rem',
      },
      {
        title: <SKUTableColumnHeaderTitle title="Avail. Supply" />,
        dataIndex: 'availableSupply',
        sorter: (left, right) => left.availableSupply - right.availableSupply,
        align: 'right',
        width: '6rem',
        apiFilterable: true,
        apiFilterType: 'number',
      },
      {
        title: <SKUTableColumnHeaderTitle title={frequencyMonthsTitle} />,
        dataIndex: 'frequency6Month',
        key: 'frequency6month',
        align: 'right',
        width: '8rem',
        sorter: (left, right) => {
          if (left.frequency6Month && right.frequency6Month) {
            return left.frequency6Month - right.frequency6Month;
          } else if (!left.frequency6Month && right.frequency6Month) {
            return -1;
          } else {
            return 1;
          }
        },
        sortDirections: ['descend', 'ascend'],
        apiFilterable: true,
        apiFilterType: 'number',
      },
      {
        title: `Total sales [${units === Units.QUANTITY ? 'MU' : currency}]`,
        dataIndex: units === Units.QUANTITY ? 'saleQuantityTotal' : 'saleValueTotal',
        render: (value: number) => value && formatNumber(value),
        sorter: (left, right) => {
          const leftValue = getTotalSalesValue(left, units);
          const rightValue = getTotalSalesValue(right, units);
          if (leftValue !== undefined && rightValue !== undefined) {
            return leftValue - rightValue;
          } else if (leftValue === undefined && rightValue !== undefined) {
            return -1;
          } else {
            return 1;
          }
        },
        align: 'right',
        width: '10rem',
        sortDirections: ['descend', 'ascend'],
        apiFilterable: true,
        apiFilterType: 'number',
      },
      {
        title: <SKUTableColumnHeaderTitle title="First purchase" />,
        dataIndex: 'firstPurchase',
        render: (value: string) => formatDate(new Date(value)),
        width: '7rem',
        sorter: (left, right) => {
          if (left.firstPurchase && right.firstPurchase) {
            return new Date(left.firstPurchase).getTime() - new Date(right.firstPurchase).getTime();
          } else if (!left.firstPurchase && right.firstPurchase) {
            return -1;
          } else {
            return 1;
          }
        },
      },

      {
        title: <SKUTableColumnHeaderTitle title="Last purchase" />,
        dataIndex: 'lastPurchase',
        render: (value: string) => formatDate(new Date(value)),
        width: '7rem',
        sorter: (left, right) => {
          if (left.lastPurchase && right.lastPurchase) {
            return new Date(left.lastPurchase).getTime() - new Date(right.lastPurchase).getTime();
          } else if (!left.lastPurchase && right.lastPurchase) {
            return -1;
          } else {
            return 1;
          }
        },
      },
      {
        title: ' ',
        dataIndex: 'actions',
        width: '3rem',
        render: (value: unknown, record: T) => {
          return (
            <ActionsBar>
              <ShowSKUAsMain skuId={record.skuId} onShowSkuAsMain={showSkuAsMain} />
            </ActionsBar>
          );
        },
      },
    ];
  }, [currency, showSkuAsMain, showStoreColumns, storeColumnProvider, units]);

  // Apply custom column modifications if provided
  const finalColumns = useMemo(() => {
    return customizeColumns ? customizeColumns(columns) : columns;
  }, [columns, customizeColumns]);

  const filterableColumns = useInjectLocalFilters<T>(finalColumns);

  const getDefaultRowClassName = useCallback(
    (record: T): string => {
      if (!record.isEnabled) {
        return 'disabled';
      } else if (record.skuId === skuId) {
        return 'highlightedRow';
      } else {
        return '';
      }
    },
    [skuId],
  );

  const rowClassProvider = useCallback(
    (record: T, index: number): string => {
      const defaultClassName = getDefaultRowClassName(record);

      const isExpanded = customExpandable?.expandedRowKeys?.includes(record.skuId);
      const expandedClassName = isExpanded ? 'expandedRow' : '';

      const combinedDefault = [defaultClassName, expandedClassName].filter(Boolean).join(' ');

      if (customRowClassName) {
        return customRowClassName(record, index, combinedDefault);
      }

      return combinedDefault;
    },
    [customRowClassName, getDefaultRowClassName, customExpandable?.expandedRowKeys],
  );

  // Create a properly typed component instance
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/naming-convention -- we need to assert here to add the $isExpandable prop
  const StyledTable = StyledSkuDetailsTable as React.ComponentType<
    React.ComponentProps<typeof ScrolledTable<T>> & { $isExpandable?: boolean }
  >;

  return (
    <ScrolledTableWrapper offset={90} minHeight={computeMinimumHeight(data.length)}>
      <StyledTable
        id="SKUTable"
        columns={filterableColumns}
        dataSource={data}
        size="small"
        rowClassName={rowClassProvider}
        rowKey="skuId"
        expandable={customExpandable}
        $isExpandable={!!customExpandable}
      />
    </ScrolledTableWrapper>
  );
};

export default SkuDetailsTable;
